import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { IconeEnLigne } from '../components/IconeEnLigne';
import { enCentimes, enEuros, formaterDuree, formaterMontant } from '../lib/format';
import { MODES_ELEMENT } from '../lib/contrat';
import { BandeauAnneeFigee } from '../components/SelecteurAnnee';
import { useAnnee } from '../hooks/useAnnee';
import type { Activite, TypeActivite } from '../types';

export function ActivitesPage() {
  const { entiteActive, peut } = useAuth();
  const [inclureInactifs, setInclureInactifs] = useState(false);
  const [modaleOuverte, setModaleOuverte] = useState(false);
  const [enEdition, setEnEdition] = useState<Activite | null>(null);

  // Referentiel ANNUEL : chaque annee porte sa grille d'activites. Une
  // annee cloturee est figee (RG-27).
  const annee = useAnnee();

  const { donnees, chargement, erreur, recharger } = useRessource(
    () =>
      annee.anneeId
        ? api.get<Activite[]>('/api/activites', { inclureInactifs, anneeId: annee.anneeId })
        : Promise.resolve([]),
    [entiteActive?.id, inclureInactifs, annee.anneeId],
  );

  const types = useRessource(
    () =>
      annee.anneeId
        ? api.get<TypeActivite[]>('/api/types-activite', { anneeId: annee.anneeId })
        : Promise.resolve([]),
    [entiteActive?.id, annee.anneeId],
  );

  const ouvrirCreation = () => {
    setEnEdition(null);
    setModaleOuverte(true);
  };

  const ouvrirEdition = (activite: Activite) => {
    if (!peut('activite.modifier') || !annee.modifiable) return;
    setEnEdition(activite);
    setModaleOuverte(true);
  };

  const basculerActivation = async (activite: Activite) => {
    await api.patch(`/api/activites/${activite.id}/activation`, { actif: !activite.actif });
    recharger();
  };

  const colonnes: Colonne<Activite>[] = [
    {
      cle: 'libelle',
      entete: 'Activité',
      rendu: (a) => (
        <span className="font-medium">
          {a.libelle}
          {/* Le globe dit « proposee en ligne », le detail en infobulle. */}
          {a.inscription_en_ligne && (
            <span className="ml-2 inline-flex align-middle">
              <IconeEnLigne />
            </span>
          )}
        </span>
      ),
    },
    {
      cle: 'type',
      entete: 'Type',
      rendu: (a) =>
        a.type_libelle ? (
          <span>
            {a.type_libelle}
            <span className="ml-2 text-xs text-encre-400">{a.type}</span>
          </span>
        ) : (
          <Badge ton="alerte">Sans type</Badge>
        ),
    },
    {
      cle: 'tarif',
      entete: 'Tarif',
      classe: 'text-right',
      rendu: (a) => (
        <span className="tabulaire">{a.tarif === null ? '—' : formaterMontant(a.tarif)}</span>
      ),
    },
    {
      cle: 'duree',
      entete: 'Durée',
      classe: 'text-right',
      rendu: (a) => (
        <span className="tabulaire">
          {formaterDuree(a.duree_minutes)}
          {a.duree_heritee && a.duree_minutes !== null && (
            <Infobulle texte="Héritée du type" className="ml-1 text-xs text-encre-400">
              ↑
            </Infobulle>
          )}
        </span>
      ),
    },
    {
      cle: 'capacite',
      entete: 'Capacité',
      classe: 'text-right',
      rendu: (a) => (
        <span className="tabulaire">
          {a.capacite_max === null ? '—' : `${a.capacite_max} pl.`}
          {a.capacite_heritee && a.capacite_max !== null && (
            <Infobulle texte="Héritée du type" className="ml-1 text-xs text-encre-400">
              ↑
            </Infobulle>
          )}
        </span>
      ),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      classe: 'text-center',
      rendu: (a) => (
        <Badge ton={a.actif ? 'succes' : 'neutre'}>{a.actif ? 'Active' : 'Désactivée'}</Badge>
      ),
    },
    {
      cle: 'creneaux',
      entete: 'Créneaux',
      classe: 'text-center',
      // Les creneaux OUVERTS de l'activite, en creux et a largeur fixe :
      // le nombre seul, les pastilles alignees d'une ligne a l'autre.
      rendu: (a) => (
        <Badge ton="neutreContour" classe="w-9 justify-center tabulaire">
          {a.nb_creneaux}
        </Badge>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (a) =>
        peut('activite.desactiver') && annee.modifiable ? (
          <button
            type="button"
            className="text-sm text-encre-500 underline hover:text-encre-900"
            onClick={(evenement) => {
              evenement.stopPropagation();
              void basculerActivation(a);
            }}
          >
            {a.actif ? 'Désactiver' : 'Réactiver'}
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Activités"
        description="L’activite porte l’entite et l’année : toute inscription héritera des deux. Une activité ne change jamais d’entité — on en crée une nouvelle et on désactive l’ancienne."
        actions={
          <span className="flex items-center gap-3">
            {peut('activite.creer') && annee.modifiable && (
              <button type="button" className="bouton-principal" onClick={ouvrirCreation}>
                Nouvelle activite
              </button>
            )}
          </span>
        }
      />

      {annee.annee && !annee.modifiable && <BandeauAnneeFigee annee={annee.annee} />}

      <div className="mb-4">
        <label className="inline-flex items-center gap-2 text-sm text-encre-600">
          <input
            type="checkbox"
            checked={inclureInactifs}
            onChange={(evenement) => setInclureInactifs(evenement.target.checked)}
          />
          Afficher les activites desactivees
        </label>
      </div>

      {erreur && <Erreur message={erreur} onReessayer={recharger} />}
      {chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={donnees ?? []}
            cleDe={(a) => a.id}
            messageVide="Aucune activite sur cette entite."
            onLigneCliquee={peut('activite.modifier') ? ouvrirEdition : undefined}
          />
        </div>
      )}

      <FormulaireActivite
        ouverte={modaleOuverte}
        activite={enEdition}
        types={types.donnees ?? []}
        onFermer={() => setModaleOuverte(false)}
        onEnregistre={() => {
          setModaleOuverte(false);
          recharger();
        }}
      />
    </>
  );
}

function FormulaireActivite({
  ouverte,
  activite,
  types,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  activite: Activite | null;
  types: TypeActivite[];
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [libelle, setLibelle] = useState('');
  const [typeActiviteId, setTypeActiviteId] = useState('');
  const [dureePropre, setDureePropre] = useState('');
  const [capacitePropre, setCapacitePropre] = useState('');
  const [baseHoraire, setBaseHoraire] = useState('');
  const [tauxHoraire, setTauxHoraire] = useState('');
  const [modeRemuneration, setModeRemuneration] = useState('');
  const [inscriptionEnLigne, setInscriptionEnLigne] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  // Reinitialise les champs a l'ouverture, sans useEffect : le rendu suit
  // l'element edite.
  const cle = ouverte ? (activite?.id ?? 'nouvelle') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setLibelle(activite?.libelle ?? '');
    setTypeActiviteId(activite?.type_activite_id ?? '');
    // Vide = « celle du type ». On ne pré-remplit donc pas avec la durée
    // héritée, sinon on la figerait sans l'avoir voulu.
    setDureePropre(
      activite && !activite.duree_heritee ? String(activite.duree_minutes ?? '') : '',
    );
    // Vide = « celle du type », comme pour la duree : pre-remplir avec la
    // valeur heritee la figerait sans qu'on l'ait voulu.
    setCapacitePropre(
      activite && !activite.capacite_heritee ? String(activite.capacite_max ?? '') : '',
    );
    setBaseHoraire(String(activite?.base_horaire_minutes ?? ''));
    setTauxHoraire(enEuros(activite?.taux_horaire));
    setModeRemuneration(activite?.mode_remuneration ?? '');
    setInscriptionEnLigne(activite?.inscription_en_ligne ?? false);
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const choisi = types.find((t) => t.id === typeActiviteId);

  // Base et taux se saisissent des qu'un MODE est choisi : le cachet se
  // calcule aussi, mois par mois, dans son onglet (2026-10-01).
  const regimeGeneral = modeRemuneration !== '';

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    // Un taux vide s'efface ; un taux saisi doit etre un montant lisible.
    // `enCentimes` rend `null` sur les deux, d'ou le test sur la saisie.
    const taux = tauxHoraire.trim() ? enCentimes(tauxHoraire) : null;
    if (tauxHoraire.trim() && taux === null) {
      setErreur('Taux horaire invalide — exemple : 26,00');
      return;
    }

    setEnCours(true);
    try {
      const corps = {
        libelle,
        typeActiviteId,
        // `null` explicite : retour à la durée du type.
        dureeMinutes: dureePropre ? Number(dureePropre) : null,
        // `null` explicite aussi : retour a la capacite du type.
        capaciteMax: capacitePropre ? Number(capacitePropre) : null,
        // `null` explicite aussi : ces trois-là doivent pouvoir être vidés.
        baseHoraireMinutes: baseHoraire ? Number(baseHoraire) : null,
        tauxHoraire: taux,
        modeRemuneration: modeRemuneration || null,
        inscriptionEnLigne,
      };
      if (activite) {
        await api.patch(`/api/activites/${activite.id}`, corps);
      } else {
        await api.post('/api/activites', corps);
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={activite ? 'Modifier l’activite' : 'Nouvelle activite'}
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        <Champ libelle="Libelle">
          <input
            className="champ"
            value={libelle}
            onChange={(evenement) => setLibelle(evenement.target.value)}
            required
            autoFocus
          />
        </Champ>

        <Champ
          libelle="Type d’activité"
          aide="Il porte le tarif et détermine la famille. Le pupitre ne sera saisissable que pour la famille Chorale (RG-05)."
        >
          <select
            className="champ"
            value={typeActiviteId}
            onChange={(evenement) => setTypeActiviteId(evenement.target.value)}
            required
          >
            <option value="">— Choisir —</option>
            {types.map((valeur) => (
              <option key={valeur.id} value={valeur.id}>
                {valeur.libelle} — {formaterMontant(valeur.tarif)} ({valeur.famille})
              </option>
            ))}
          </select>
        </Champ>

        {choisi?.commentaire && (
          <p className="-mt-2 rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
            {choisi.commentaire}
          </p>
        )}

        <Champ
          libelle="Durée d’une séance (minutes)"
          aide={
            choisi
              ? `Laissez vide pour reprendre celle du type : ${formaterDuree(choisi.duree_minutes)}. À renseigner seulement si cette activité en diffère — l’Éveil musical varie de 40 à 50 minutes selon la section.`
              : 'Choisissez d’abord un type.'
          }
        >
          <input
            className="champ tabulaire"
            type="number"
            min={5}
            max={480}
            step={5}
            placeholder={choisi ? String(choisi.duree_minutes) : ''}
            value={dureePropre}
            onChange={(evenement) => setDureePropre(evenement.target.value)}
          />
        </Champ>

        <Champ
          libelle="Capacité maximale (places)"
          aide={
            choisi?.capacite_max
              ? `Laissez vide pour reprendre celle du type : ${choisi.capacite_max} places. Une activité peut accueillir MOINS que son type, jamais plus.`
              : choisi
                ? 'Ce type ne fixe pas de capacité : celle saisie ici s’appliquera seule.'
                : 'Choisissez d’abord un type.'
          }
        >
          <input
            className="champ tabulaire"
            type="number"
            min={1}
            max={choisi?.capacite_max ?? 999}
            placeholder={choisi?.capacite_max ? String(choisi.capacite_max) : ''}
            value={capacitePropre}
            onChange={(evenement) => setCapacitePropre(evenement.target.value)}
          />
        </Champ>

        {/* Ce que l'activite coute a l'employeur, par opposition au tarif
            du type, qui est ce qu'elle rapporte. Les deux ne se deduisent
            pas l'un de l'autre — ni de la duree de la seance. */}
        <fieldset className="space-y-4 rounded-md border border-encre-200 p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-encre-500">
            Éléments de rémunération
          </legend>

          {/* Le MODE d'abord (2026-09-30) : c'est lui qui dit si la base
              et le taux ont un sens. Les elements de paie ne concernent
              que le regime general — une activite au cachet est declaree
              via le GUSO, hors de l'outil. */}
          <Champ
            libelle="Mode de rémunération"
            aide="Permanent : régime général, déclaré au CEA. Intermittent : cachet, déclaré via le GUSO. Les deux produisent des éléments de paie, chacun dans son onglet."
          >
            <select
              className="champ"
              value={modeRemuneration}
              onChange={(evenement) => setModeRemuneration(evenement.target.value)}
            >
              <option value="">— non renseigné —</option>
              {MODES_ELEMENT.map((m) => (
                <option key={m.valeur} value={m.valeur}>
                  {m.libelle}
                </option>
              ))}
            </select>
          </Champ>

          {/* Base et taux attendent qu'un mode soit choisi. Desactives, ils
              GARDENT leur valeur. */}
          <div className="grid grid-cols-2 gap-3">
            <Champ
              libelle="Base horaire (min)"
              aide="Temps rémunéré d’une séance, qui n’est pas sa durée : un solfège de 50 minutes est payé une heure."
            >
              <input
                className="champ tabulaire disabled:bg-encre-100 disabled:text-encre-400"
                type="number"
                min={5}
                max={480}
                step={5}
                placeholder="60"
                value={baseHoraire}
                disabled={!regimeGeneral}
                onChange={(evenement) => setBaseHoraire(evenement.target.value)}
              />
            </Champ>

            <Champ libelle="Taux horaire (€)" aide="Prix de l’heure enseignée. 24 ou 26 € selon l’activité.">
              <input
                className="champ tabulaire disabled:bg-encre-100 disabled:text-encre-400"
                inputMode="decimal"
                placeholder="26,00"
                value={tauxHoraire}
                disabled={!regimeGeneral}
                onChange={(evenement) => setTauxHoraire(evenement.target.value)}
              />
            </Champ>
          </div>

          <p className="text-xs text-encre-500">
            La base et le taux se saisissent une fois le mode choisi. Laissez-les vides tant que
            la valeur n’est pas connue : une activité sans taux n’est pas rémunérée à zéro euro,
            elle attend sa saisie.
          </p>
        </fieldset>

        {/* Le formulaire public ne propose que les activites cochees :
            un reglage par activite, pas une regle sur la famille. */}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={inscriptionEnLigne}
            onChange={(evenement) => setInscriptionEnLigne(evenement.target.checked)}
          />
          <span>
            Proposée à l’inscription en ligne
            <span className="block text-xs text-encre-400">
              Ses créneaux apparaissent sur le formulaire public des adultes (chorales, orchestre).
            </span>
          </span>
        </label>

        {/* Plus de champ « Ordre d'affichage » (2026-10-12) : les
            activites se classent par l'ordre de leur TYPE — pose en
            glissant les lignes de l'ecran Types d'activite —, puis par
            l'alphabet. */}

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {erreur}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="bouton-principal" disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modale>
  );
}
